import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { Stage, Layer, Line, Circle, Group, Text, Arrow, Rect } from 'react-konva'
import { useTheme } from 'styled-components'
import {
  FiMousePointer,
  FiSquare,
  FiMinus,
  FiLayers,
  FiGrid,
  FiTrendingDown,
  FiArrowDown,
  FiZoomIn,
  FiZoomOut,
  FiMaximize2,
  FiMaximize,
  FiMinimize,
  FiTrash2,
  FiActivity,
  FiMove,
  FiSave,
  FiUpload,
  FiPlay,
  FiSliders,
  FiCheck,
  FiX,
  FiBox,
} from 'react-icons/fi'
import {
  CanvasWrapper,
  Toolbar,
  ToolButton,
  ToolbarSeparator,
  ToolbarBreak,
  HintBar,
  CanvasBody,
  StageHost,
  PropertiesPanel,
  PropertyRow,
  PropertiesEmptyState,
  NearbyList,
  NearbyItemButton,
} from './canvasStyles'
import {
  createPillar,
  createBeam,
  createSlab,
  createAreaLoad,
  createLineLoad,
  createPointLoad,
  createBoundaryEdge,
  cascadeRemoveSlab,
  cascadeRemoveBeam,
  renumberModel,
} from './model'
import {
  computeAllDiagrams,
  computeDeformedElementPoints,
  computeDeformedElementPoints3D,
  computeDeformedScale,
  computeMeshAlignments,
  estimateTributaryWidth,
  buildAlignmentProfile,
  isParallelDir,
} from './diagrams'
import { findBeamFaceAt } from './beamFace'
import { roundToDecimals } from '../../../utils/numberFormat'
import { Spinner } from '../../../components/Spinner'
import { FormErrorsBox } from './styles'
import { ViewOptionsPanel } from './ViewOptionsPanel'

const BASE_SCALE = 60
const GRID_EXTENT = 30
const DEFAULT_SNAP = 0.25
const DEFAULT_GRID_SPACING = 1
const MAX_GRID_LINES = 160
const STAGE_HEIGHT = 480
const MIN_ZOOM = 0.2
const MAX_ZOOM = 16

function snap(value, size) {
  const step = Number(size) > 0 ? Number(size) : DEFAULT_SNAP
  return Math.round(value / step) * step
}

function positiveScale(value) {
  const n = Number(value)
  return n > 0 ? n : 1
}

function modelToWorld(x, y) {
  return { x: x * BASE_SCALE, y: -y * BASE_SCALE }
}

function worldToModel(x, y) {
  return { x: x / BASE_SCALE, y: -y / BASE_SCALE }
}

// True axonometric (30°) projection for the isometric deformed-shape view: model x runs down-right
// on screen, model y runs down-left, and z (vertical/out-of-plane displacement) runs straight up —
// the standard isometric axis layout. Only used for that read-only view; every other render in
// this file stays on the plain x/y plan projection above.
const ISO_COS30 = Math.cos(Math.PI / 6)
const ISO_SIN30 = Math.sin(Math.PI / 6)

function modelToIsoWorld(x, y, z = 0) {
  return {
    x: (x - y) * ISO_COS30 * BASE_SCALE,
    y: ((x + y) * ISO_SIN30 - z) * BASE_SCALE,
  }
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function interpolateAt(xs, values, xTarget) {
  if (xTarget <= xs[0]) return values[0]
  if (xTarget >= xs[xs.length - 1]) return values[values.length - 1]
  for (let i = 1; i < xs.length; i += 1) {
    if (xTarget <= xs[i]) {
      const span = xs[i] - xs[i - 1] || 1
      const t = (xTarget - xs[i - 1]) / span
      return values[i - 1] + (values[i] - values[i - 1]) * t
    }
  }
  return values[values.length - 1]
}

// Diverging blue (negative) → light neutral (zero) → red (positive) color scale, normalized by
// the signed min/max across the whole mesh for the active diagram type. Used to color the mesh
// wireframe as a heatmap instead of stacking per-element ribbons, which overlap unreadably once
// the mesh has more than a handful of elements (see the block that calls this).
function diagramValueColor(value, range) {
  if (!range || !Number.isFinite(range.min) || !Number.isFinite(range.max)) return '#94a3b8'
  const extent = Math.max(Math.abs(range.min), Math.abs(range.max))
  if (extent === 0) return 'hsl(210, 15%, 70%)'
  const t = clamp(Math.abs(value) / extent, 0, 1)
  const hue = value >= 0 ? 6 : 214
  const lightness = 78 - t * 45
  return `hsl(${hue}, 82%, ${lightness}%)`
}

// A standalone position→value chart for one isolated alignment (see buildAlignmentProfile in
// diagrams.js), drawn in the properties panel — away from the plan view entirely, since a value
// stacked directly on top of the geometry is exactly what got unreadable with a dense mesh.
function AlignmentChart({ profile, mode, perMeter, theme }) {
  const width = 248
  const height = 150
  const marginX = 32
  const marginY = 16
  if (!profile || profile.length === 0) return null

  const isDisplacement = mode === 'w'
  const values = profile.map((p) => {
    const v = mode === 'M' ? -p[mode] : p[mode]
    return isDisplacement ? v * 100 : v
  })
  const positions = profile.map((p) => p.position)
  const maxPos = Math.max(...positions, 1e-6)
  const minV = Math.min(0, ...values)
  const maxV = Math.max(0, ...values)
  const spanV = maxV - minV || 1

  const xScale = (pos) => marginX + (pos / maxPos) * (width - marginX * 2)
  const yScale = (v) => height - marginY - ((v - minV) / spanV) * (height - marginY * 2)

  const linePoints = profile.map((p, i) => `${xScale(p.position)},${yScale(values[i])}`).join(' ')
  const fillPoints = `${xScale(0)},${yScale(0)} ${linePoints} ${xScale(maxPos)},${yScale(0)}`
  const unit = isDisplacement ? ' cm' : perMeter ? '/m' : ''
  // Labels must show the real M/V/T value at each screen position, not the flipped `values` used
  // to draw the curve — for M mode, the top of the chart (where `values` is largest) corresponds
  // to the smallest real M, and vice versa, since `values = -M` there.
  const topValue = mode === 'M' ? -maxV : maxV
  const bottomValue = mode === 'M' ? -minV : minV

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <line
        x1={marginX}
        y1={yScale(0)}
        x2={width - marginX}
        y2={yScale(0)}
        stroke={theme.colors.border}
        strokeWidth={1}
      />
      <polygon points={fillPoints} fill={`${theme.colors.primary}22`} stroke="none" />
      <polyline
        points={linePoints}
        fill="none"
        stroke={theme.colors.primary}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text x={marginX} y={height - 2} fontSize="10" fill={theme.colors.textSecondary}>
        0
      </text>
      <text x={width - marginX} y={height - 2} fontSize="10" fill={theme.colors.textSecondary} textAnchor="end">
        {roundToDecimals(maxPos, 2)} m
      </text>
      <text x={2} y={Math.max(yScale(maxV), 10)} fontSize="10" fill={theme.colors.textSecondary}>
        {roundToDecimals(topValue, 2)}
        {unit}
      </text>
      <text x={2} y={Math.min(yScale(minV) + 4, height - marginY + 10)} fontSize="10" fill={theme.colors.textSecondary}>
        {roundToDecimals(bottomValue, 2)}
        {unit}
      </text>
    </svg>
  )
}

function pointToSegmentDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1
  const dy = y2 - y1
  const lengthSq = dx * dx + dy * dy
  if (lengthSq === 0) return Math.hypot(px - x1, py - y1)
  const t = clamp(((px - x1) * dx + (py - y1) * dy) / lengthSq, 0, 1)
  const projX = x1 + t * dx
  const projY = y1 + t * dy
  return Math.hypot(px - projX, py - projY)
}

function elementVectors(nodeI, nodeJ) {
  const dx = nodeJ.x - nodeI.x
  const dy = nodeJ.y - nodeI.y
  const length = Math.hypot(dx, dy) || 1
  const ux = dx / length
  const uy = dy / length
  return { ux, uy, nx: -uy, ny: ux, length }
}

// Ray-casting point-in-polygon test (model coordinates), used to pick which slab a click/area
// load lands in.
function pointInPolygon(point, polygon) {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x
    const yi = polygon[i].y
    const xj = polygon[j].x
    const yj = polygon[j].y
    const intersect = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

function polygonCentroid(polygon) {
  const n = polygon.length || 1
  const sx = polygon.reduce((s, p) => s + p.x, 0)
  const sy = polygon.reduce((s, p) => s + p.y, 0)
  return { x: sx / n, y: sy / n }
}

// --- Marquee (rubber-band) selection geometry, all in world coordinates ---------------------
function normalizeRect(x0, y0, x1, y1) {
  return { minX: Math.min(x0, x1), minY: Math.min(y0, y1), maxX: Math.max(x0, x1), maxY: Math.max(y0, y1) }
}

function pointInRect(rect, x, y) {
  return x >= rect.minX && x <= rect.maxX && y >= rect.minY && y <= rect.maxY
}

function orient(ax, ay, bx, by, cx, cy) {
  return (bx - ax) * (cy - ay) - (by - ay) * (cx - ax)
}

function segmentsIntersect(p1, p2, p3, p4) {
  const o1 = orient(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)
  const o2 = orient(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y)
  const o3 = orient(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y)
  const o4 = orient(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y)
  return o1 > 0 !== o2 > 0 && o3 > 0 !== o4 > 0
}

// True when segment a-b touches the rect at all: either endpoint inside, or the segment crosses
// any of the four rect edges. Used for AutoCAD-style "crossing" (right-to-left) selection.
function segmentIntersectsRect(rect, a, b) {
  if (pointInRect(rect, a.x, a.y) || pointInRect(rect, b.x, b.y)) return true
  const c = [
    { x: rect.minX, y: rect.minY },
    { x: rect.maxX, y: rect.minY },
    { x: rect.maxX, y: rect.maxY },
    { x: rect.minX, y: rect.maxY },
  ]
  return (
    segmentsIntersect(a, b, c[0], c[1]) ||
    segmentsIntersect(a, b, c[1], c[2]) ||
    segmentsIntersect(a, b, c[2], c[3]) ||
    segmentsIntersect(a, b, c[3], c[0])
  )
}

// A polygon "touches" the rect (crossing selection) when any vertex is inside it or any edge
// crosses one of its sides; it's "fully enclosed" (window selection) when every vertex is inside.
function polygonTouchesRect(rect, worldPoints) {
  if (worldPoints.some((p) => pointInRect(rect, p.x, p.y))) return true
  return worldPoints.some((p, i) => segmentIntersectsRect(rect, p, worldPoints[(i + 1) % worldPoints.length]))
}

function polygonInsideRect(rect, worldPoints) {
  return worldPoints.every((p) => pointInRect(rect, p.x, p.y))
}

const HINTS = {
  select: 'Clique para selecionar/editar. Arraste um vértice, extremidade de viga ou pilar para movê-lo.',
  pillar: 'Clique em um ponto vazio do canvas para posicionar um pilar.',
  beam: 'Clique no ponto inicial e depois no ponto final da viga.',
  slab: 'Clique dentro de um contorno fechado de vigas para criar a laje automaticamente. Se não houver um contorno fechado ali, o clique inicia o desenho manual do polígono (mín. 3 vértices; duplo clique ou "Concluir" para fechar, Esc cancela).',
  areaLoad: 'Clique dentro de uma laje para adicionar uma carga de área nela.',
  lineLoad: 'Clique perto de uma viga para adicionar uma carga linear nela.',
  pointLoad: 'Clique em qualquer ponto do canvas para adicionar uma carga pontual.',
  drawPolygon: 'Clique para adicionar vértices. Duplo clique ou "Concluir" para fechar, Esc cancela.',
}

const DIAGRAM_MODE_LABELS = { V: 'Cortante (V)', M: 'Momento (M)', T: 'Torção (T)', w: 'Deslocamento (w)' }

export function StructureCanvas({
  pillars,
  beams,
  slabs,
  loads,
  meshMaxSpacing,
  setPillars,
  setBeams,
  setSlabs,
  setLoads,
  setMeshMaxSpacing,
  disabled,
  result,
  formErrors,
  isFormValid,
  canCalculate,
  onCalculate,
  onSave,
  onFileUpload,
}) {
  const theme = useTheme()
  const stageRef = useRef(null)
  const hostRef = useRef(null)
  const wrapperRef = useRef(null)
  const fileInputRef = useRef(null)
  const panRef = useRef(null)
  // Marquee drag origin (world coords) while a rubber-band selection is in progress; a click that
  // barely moves is treated as a normal pick instead. `suppressClickRef` swallows the click event
  // Konva fires right after a real marquee drag so it doesn't immediately re-select a single item.
  const marqueeStartRef = useRef(null)
  const suppressClickRef = useRef(false)

  const [mode, setMode] = useState('select')
  const [scale, setScale] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [stagePos, setStagePos] = useState({ x: 260, y: 260 })
  const [selected, setSelected] = useState(null)
  // Multi-selection from a marquee: a list of { type, key }. Kept separate from the single
  // `selected` (which drives the property editor); a marquee sets this and clears `selected`,
  // while any single pick clears this (see the effect below).
  const [multiSelection, setMultiSelection] = useState([])
  // Live marquee rectangle while dragging: { x0, y0, x1, y1 } in world coords, plus `crossing`
  // when the drag goes right-to-left (AutoCAD crossing vs. window semantics).
  const [marquee, setMarquee] = useState(null)
  const [nearbyCandidates, setNearbyCandidates] = useState([])
  const [pendingBeamPoint, setPendingBeamPoint] = useState(null)
  // Generic polygon-drawing state, shared by "new slab" and "area load sub-region" gestures.
  // `target` is either 'newSlab' or { type: 'loadPolygon', key }.
  const [drawingPolygon, setDrawingPolygon] = useState(null)
  const [stageWidth, setStageWidth] = useState(800)
  const [stageHeight, setStageHeight] = useState(STAGE_HEIGHT)
  const [diagramMode, setDiagramMode] = useState('none')
  const [snapSize, setSnapSize] = useState(DEFAULT_SNAP)
  const [gridSpacingX, setGridSpacingX] = useState(DEFAULT_GRID_SPACING)
  const [gridSpacingY, setGridSpacingY] = useState(DEFAULT_GRID_SPACING)
  const [viewScales, setViewScales] = useState({ deformed: '1', diagram: '1', node: '1', support: '1', text: '1' })
  const [queryMarker, setQueryMarker] = useState(null)
  // One or more isolated "lines of bars" (see diagrams.js#computeMeshAlignments), so the diagram
  // can be decluttered to just those alignments — empty shows the whole mesh as usual. A plain
  // click replaces the selection; shift-click appends another alignment collinear with the first
  // (e.g. the matching row in a neighboring slab split off by a crossing beam), so the properties
  // panel stitches both into one combined diagram — see buildAlignmentProfile in diagrams.js.
  const [sectionAlignments, setSectionAlignments] = useState([])
  const [perMeter, setPerMeter] = useState(false)
  const [showReactions, setShowReactions] = useState(true)
  const [showDisplacements, setShowDisplacements] = useState(false)
  // Replaces the whole canvas with a read-only isometric (3D-looking) render of just the deformed
  // shape — see modelToIsoWorld/isometricDeformedShape. Editing tools don't apply in this view.
  const [isometricView, setIsometricView] = useState(false)
  const [showLoads, setShowLoads] = useState(true)
  const [showLabels, setShowLabels] = useState(true)
  const [showMesh, setShowMesh] = useState(true)
  const [showViewOptions, setShowViewOptions] = useState(false)
  const [showPillars, setShowPillars] = useState(true)
  const [showBeams, setShowBeams] = useState(true)
  const [showSlabs, setShowSlabs] = useState(true)
  const [showBoundaryEdges, setShowBoundaryEdges] = useState(true)

  const hasResult = Boolean(result?.model)

  // Any single pick (click on a shape, placement) sets `selected` to a truthy value — that
  // supersedes a marquee selection, so drop it. A marquee itself sets `selected` to null, so it
  // never trips this.
  useEffect(() => {
    if (selected) setMultiSelection([])
  }, [selected])

  const modelNodesById = useMemo(() => {
    const map = {}
    ;(result?.model?.nodes || []).forEach((n) => {
      map[n.id] = n
    })
    return map
  }, [result])

  // World-space bounding box of the whole mesh, used to park the query-marker label clear of the
  // structure (above it for horizontal bars, to its left for vertical ones) instead of on top of
  // the diagram line it's reading from.
  const structureWorldBounds = useMemo(() => {
    const nodes = result?.model?.nodes || []
    if (nodes.length === 0) return null
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity
    nodes.forEach((n) => {
      const w = modelToWorld(n.x, n.y)
      if (w.x < minX) minX = w.x
      if (w.x > maxX) maxX = w.x
      if (w.y < minY) minY = w.y
      if (w.y > maxY) maxY = w.y
    })
    return { minX, maxX, minY, maxY }
  }, [result])

  // The backend already returns each generated element's full V/M/T/w diagram; it's matched back
  // to the discretized mesh (result.model.elements), not the physical beams the user drew.
  const diagramInfo = useMemo(() => {
    if (!hasResult) return null
    return computeAllDiagrams(result.model.elements || [], result.elements || [])
  }, [hasResult, result])

  // Partitions the mesh into straight "lines of bars" once per result — reused both to isolate a
  // line on click (sectionElementIds) and to estimate each element's tributary width for the "por
  // metro" normalization below.
  const meshAlignments = useMemo(() => {
    if (!hasResult) return []
    return computeMeshAlignments(result.model.elements || [], modelNodesById)
  }, [hasResult, result, modelNodesById])

  // V/M/T as the backend reports them are totals for an element's own tributary width, not a
  // per-meter rate — dividing each by the distance to its neighboring parallel lines (see
  // estimateTributaryWidth) converts them to the per-meter values engineers compare against slab
  // design charts. Displacement (w) is untouched — it isn't a per-width quantity.
  const displayDiagramInfo = useMemo(() => {
    if (!diagramInfo) return null
    if (!perMeter) return diagramInfo
    const fallbackWidth = Number(meshMaxSpacing) || 1
    const diagramsByElementId = {}
    const maxAbs = { V: 0, M: 0, T: 0, w: diagramInfo.maxAbs.w }
    const range = {
      V: { min: Infinity, max: -Infinity },
      M: { min: Infinity, max: -Infinity },
      T: { min: Infinity, max: -Infinity },
      w: diagramInfo.range.w,
    }
    Object.entries(diagramInfo.diagramsByElementId).forEach(([elId, diagram]) => {
      const width = estimateTributaryWidth(elId, meshAlignments, fallbackWidth) || fallbackWidth
      const scaled = diagram.map((p) => {
        const next = { ...p }
        ;['V', 'M', 'T'].forEach((type) => {
          next[type] = (Number(p[type]) || 0) / width
        })
        return next
      })
      diagramsByElementId[elId] = scaled
      ;['V', 'M', 'T'].forEach((type) => {
        scaled.forEach((p) => {
          const v = p[type]
          if (Math.abs(v) > maxAbs[type]) maxAbs[type] = Math.abs(v)
          if (v < range[type].min) range[type].min = v
          if (v > range[type].max) range[type].max = v
        })
      })
    })
    return { diagramsByElementId, maxAbs, range }
  }, [diagramInfo, perMeter, meshAlignments, meshMaxSpacing])

  useEffect(() => {
    if (queryMarker && !diagramInfo?.diagramsByElementId?.[queryMarker.elementId]) setQueryMarker(null)
  }, [diagramInfo, queryMarker])

  useEffect(() => {
    if (sectionAlignments.length > 0 && !diagramInfo) setSectionAlignments([])
  }, [diagramInfo, sectionAlignments])

  // Flat element-id list across every selected alignment, for highlighting and the "Ver tudo"
  // count — null (not just empty) when nothing is selected, matching the old single-alignment API.
  const sectionElementIds = useMemo(
    () => (sectionAlignments.length > 0 ? sectionAlignments.flatMap((a) => a.elementIds) : null),
    [sectionAlignments]
  )

  // Stitches every selected alignment's per-element diagrams into one continuous position→value
  // profile, for plotting as a standalone chart in the properties panel — away from the plan
  // view, not overlaid on the geometry (see buildAlignmentProfile in diagrams.js). When more than
  // one alignment is selected (shift-click), they're merged into a single pseudo-alignment sharing
  // the first one's direction — buildAlignmentProfile positions samples by real projected distance
  // along that axis, so it naturally bridges the gap left by whatever crossing beam split them.
  const alignmentProfile = useMemo(() => {
    if (sectionAlignments.length === 0 || !displayDiagramInfo || !hasResult) return null
    const merged = { elementIds: sectionAlignments.flatMap((a) => a.elementIds), dir: sectionAlignments[0].dir }
    return buildAlignmentProfile(
      merged,
      result.model.elements || [],
      modelNodesById,
      displayDiagramInfo.diagramsByElementId
    )
  }, [sectionAlignments, displayDiagramInfo, hasResult, result, modelNodesById])

  // Auto-scale so the largest transverse displacement (w) reads as ~8% of the structure's size.
  const deformedShape = useMemo(() => {
    if (!hasResult || !diagramInfo) return null

    const nodes = result.model.nodes || []
    const maxDisp = diagramInfo.maxAbs.w || 0
    const scale = computeDeformedScale(nodes, maxDisp, positiveScale(viewScales.deformed))

    const polylines = []
    ;(result.model.elements || []).forEach((el) => {
      const nodeI = modelNodesById[el.nodeI]
      const nodeJ = modelNodesById[el.nodeJ]
      const diagram = diagramInfo.diagramsByElementId[el.id]
      if (!nodeI || !nodeJ || !diagram) return
      const modelPoints = computeDeformedElementPoints(nodeI, nodeJ, diagram, scale)
      const worldPoints = modelPoints.map((p) => modelToWorld(p.x, p.y))
      polylines.push({ id: el.id, points: worldPoints.flatMap((w) => [w.x, w.y]) })
    })

    return { polylines, scale }
  }, [hasResult, diagramInfo, result, modelNodesById, viewScales.deformed])

  // Same amplified deflected shape as `deformedShape` above, but projected with a real isometric
  // (x/y/z) transform instead of the flat x/y plan — see modelToIsoWorld. Only used by the
  // isometric view toggle, which replaces the whole canvas with just this shape (see `isometricView`
  // below): the user asked for the deformed shape alone there, without the plan geometry as
  // reference, since the isometric axes already convey scale and position.
  const isometricDeformedShape = useMemo(() => {
    if (!hasResult || !diagramInfo) return null

    const nodes = result.model.nodes || []
    const maxDisp = diagramInfo.maxAbs.w || 0
    const scale = computeDeformedScale(nodes, maxDisp, positiveScale(viewScales.deformed))

    const polylines = []
    ;(result.model.elements || []).forEach((el) => {
      const nodeI = modelNodesById[el.nodeI]
      const nodeJ = modelNodesById[el.nodeJ]
      const diagram = diagramInfo.diagramsByElementId[el.id]
      if (!nodeI || !nodeJ || !diagram) return
      const modelPoints = computeDeformedElementPoints3D(nodeI, nodeJ, diagram, scale)
      const worldPoints = modelPoints.map((p) => modelToIsoWorld(p.x, p.y, p.z))
      polylines.push({ id: el.id, points: worldPoints.flatMap((w) => [w.x, w.y]) })
    })

    return { polylines, scale }
  }, [hasResult, diagramInfo, result, modelNodesById, viewScales.deformed])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      setStageWidth(width)
      setStageHeight(height)
    })
    observer.observe(host)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    function handleFullscreenChange() {
      setIsFullscreen(document.fullscreenElement === wrapperRef.current)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      wrapperRef.current?.requestFullscreen()
    }
  }

  function selectMode(nextMode) {
    setMode(nextMode)
    setPendingBeamPoint(null)
    setDrawingPolygon(null)
    setSelected(null)
    setMultiSelection([])
    setNearbyCandidates([])
  }

  function startPolygonDraw(target) {
    setMode('drawPolygon')
    setDrawingPolygon({ points: [], target })
    setSelected(null)
    setMultiSelection([])
    setNearbyCandidates([])
  }

  function cancelPolygonDraw() {
    setDrawingPolygon(null)
    setMode('select')
  }

  function finishPolygonDraw() {
    setDrawingPolygon((current) => {
      if (!current || current.points.length < 3) return current
      const polygon = current.points
      if (current.target === 'newSlab') {
        const newSlab = createSlab(slabs, polygon)
        commitModel({ slabs: [...slabs, newSlab] })
        setSelected({ type: 'slab', key: newSlab.key })
      } else if (current.target?.type === 'loadPolygon') {
        const key = current.target.key
        setLoads((prev) => prev.map((l) => (l.key === key ? { ...l, polygon } : l)))
        setSelected({ type: 'load', key })
      }
      setMode('select')
      return null
    })
  }

  function zoomBy(factor) {
    setScale((prev) => clamp(prev * factor, MIN_ZOOM, MAX_ZOOM))
  }

  function resetView() {
    setScale(1)
    setStagePos({ x: 260, y: 260 })
  }

  const handleWheel = useCallback((e) => {
    e.evt.preventDefault()
    const stage = stageRef.current
    if (!stage) return
    const oldScale = stage.scaleX()
    const pointer = stage.getPointerPosition()
    if (!pointer) return
    const mousePointTo = { x: (pointer.x - stage.x()) / oldScale, y: (pointer.y - stage.y()) / oldScale }
    const direction = e.evt.deltaY > 0 ? -1 : 1
    const newScale = clamp(oldScale * (direction > 0 ? 1.08 : 0.93), MIN_ZOOM, MAX_ZOOM)
    setScale(newScale)
    setStagePos({ x: pointer.x - mousePointTo.x * newScale, y: pointer.y - mousePointTo.y * newScale })
  }, [])

  // Snaps a placement click to a nearby pillar center or beam endpoint (within a small pixel
  // radius), so drawing a beam onto an existing pillar lands exactly on its coordinates instead
  // of a hair off it — which would otherwise leave the two disconnected once the API discretizes
  // the mesh (connectivity there is resolved purely by shared coordinates).
  function findNearbyAnchor(pointer) {
    const threshold = 12 / scale
    let best = null
    let bestDistance = threshold
    pillars.forEach((p) => {
      const w = modelToWorld(p.x, p.y)
      const d = Math.hypot(pointer.x - w.x, pointer.y - w.y)
      if (d < bestDistance) {
        bestDistance = d
        best = { x: Number(p.x), y: Number(p.y) }
      }
    })
    beams.forEach((b) => {
      ;[
        { x: Number(b.xI), y: Number(b.yI) },
        { x: Number(b.xJ), y: Number(b.yJ) },
      ].forEach((pt) => {
        const w = modelToWorld(pt.x, pt.y)
        const d = Math.hypot(pointer.x - w.x, pointer.y - w.y)
        if (d < bestDistance) {
          bestDistance = d
          best = pt
        }
      })
    })
    return best
  }

  // Applies a partial model change (only the arrays that actually changed need to be passed) and
  // renumbers everything to a contiguous P1../V1../L1../Q1../LN1../C1.. sequence in one shot, so
  // ids never drift out of sync or collide after a create/delete (see model.js#renumberModel).
  function commitModel(partial) {
    const next = renumberModel({
      pillars: partial.pillars ?? pillars,
      beams: partial.beams ?? beams,
      slabs: partial.slabs ?? slabs,
      loads: partial.loads ?? loads,
    })
    setPillars(next.pillars)
    setBeams(next.beams)
    setSlabs(next.slabs)
    setLoads(next.loads)
    return next
  }

  function findSelectableCandidates(pointer) {
    const threshold = 14 / scale
    const candidates = []

    function consider(distance, candidate, customThreshold) {
      if (distance < (customThreshold ?? threshold)) candidates.push({ ...candidate, distance })
    }

    if (showPillars) {
      pillars.forEach((p) => {
        const w = modelToWorld(p.x, p.y)
        consider(Math.hypot(pointer.x - w.x, pointer.y - w.y), { type: 'pillar', key: p.key, label: `Pilar ${p.id}` })
      })
    }

    if (showBeams) {
      beams.forEach((b) => {
        const a = modelToWorld(b.xI, b.yI)
        const c = modelToWorld(b.xJ, b.yJ)
        consider(pointToSegmentDistance(pointer.x, pointer.y, a.x, a.y, c.x, c.y), {
          type: 'beam',
          key: b.key,
          label: `Viga ${b.id}`,
        })
      })
    }

    if (showSlabs) {
      slabs.forEach((s) => {
        const model = worldToModel(pointer.x, pointer.y)
        if (pointInPolygon(model, s.polygon)) {
          consider(threshold * 0.99, { type: 'slab', key: s.key, label: `Laje ${s.id}` })
        }
      })
    }

    loads.forEach((l) => {
      if (!showLoads) return
      let anchor = null
      if (l.type === 'point') {
        anchor = { x: l.x, y: l.y }
      } else if (l.type === 'line') {
        const beam = beams.find((b) => b.id === l.beam)
        if (beam) {
          const length = Math.hypot(beam.xJ - beam.xI, beam.yJ - beam.yI) || 1
          const start = l.start === '' || l.start === undefined ? 0 : Number(l.start)
          const end = l.end === '' || l.end === undefined ? length : Number(l.end)
          const t = clamp((start + end) / 2, 0, length) / length
          anchor = { x: beam.xI + (beam.xJ - beam.xI) * t, y: beam.yI + (beam.yJ - beam.yI) * t }
        }
      } else {
        const slab = slabs.find((s) => s.id === l.slab)
        const polygon = Array.isArray(l.polygon) && l.polygon.length >= 3 ? l.polygon : slab?.polygon
        if (polygon) anchor = polygonCentroid(polygon)
      }
      if (!anchor) return
      const w = modelToWorld(anchor.x, anchor.y)
      consider(Math.hypot(pointer.x - w.x, pointer.y - w.y), {
        type: 'load',
        key: l.key,
        label: `Carga ${l.id} (${l.type})`,
      })
    })

    if (showBoundaryEdges && showSlabs) {
      slabs.forEach((s) => {
        s.polygon.forEach((p, edgeIdx) => {
          const nextIdx = (edgeIdx + 1) % s.polygon.length
          const nextP = s.polygon[nextIdx]
          const w1 = modelToWorld(p.x, p.y)
          const w2 = modelToWorld(nextP.x, nextP.y)
          const dist = pointToSegmentDistance(pointer.x, pointer.y, w1.x, w1.y, w2.x, w2.y)
          const edgeConfig = s.boundaryEdges?.find((e) => Number(e.edge) === edgeIdx)
          consider(dist, {
            type: 'slabEdge',
            key: `edge-${s.key}-${edgeIdx}`,
            slabKey: s.key,
            edgeIndex: edgeIdx,
            label: edgeConfig ? `Bordo ${s.id} (${edgeConfig.id})` : `Bordo ${s.id} (Borda ${edgeIdx})`,
          })
        })
      })
    }

    return candidates.sort((a, b) => a.distance - b.distance)
  }

  function handleStageClick() {
    if (disabled) return

    // Konva fires a click right after a marquee drag ends; ignore that one so the drag's
    // selection isn't immediately replaced by a single pick at the release point.
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }

    const stage = stageRef.current
    const pointer = stage.getRelativePointerPosition()
    const model = worldToModel(pointer.x, pointer.y)
    const anchor = findNearbyAnchor(pointer)
    const sx = anchor ? anchor.x : snap(model.x, snapSize)
    const sy = anchor ? anchor.y : snap(model.y, snapSize)

    // Placement modes must work even when the click lands on top of an existing pillar/beam/slab
    // shape (e.target is that shape, not the stage, in that case) — only a plain "select" pick
    // needs to distinguish empty canvas from a shape, and that's handled separately below.
    if (mode === 'drawPolygon') {
      setDrawingPolygon((prev) => ({ ...prev, points: [...prev.points, { x: sx, y: sy }] }))
      return
    }

    if (mode === 'select') {
      const candidates = findSelectableCandidates(pointer)
      setNearbyCandidates(candidates)
      selectCandidate(candidates[0] || null)
      // A plain click supersedes a marquee selection, even when it hits empty canvas (which
      // leaves `selected` null, so the clear-on-select effect wouldn't fire).
      setMultiSelection([])
      // Clicking blank canvas is the "deselect everything" gesture, so it also drops the section
      // cut and its query marker — same effect as the "Ver tudo" button, without needing it.
      if (candidates.length === 0) {
        setSectionAlignments([])
        setQueryMarker(null)
      }
      return
    }

    if (mode === 'pillar') {
      const newPillar = createPillar(pillars, sx, sy)
      commitModel({ pillars: [...pillars, newPillar] })
      setSelected({ type: 'pillar', key: newPillar.key })
    } else if (mode === 'beam') {
      if (!pendingBeamPoint) {
        setPendingBeamPoint({ x: sx, y: sy })
      } else {
        const newBeam = createBeam(beams, pendingBeamPoint.x, pendingBeamPoint.y, sx, sy)
        commitModel({ beams: [...beams, newBeam] })
        setPendingBeamPoint(null)
        setSelected({ type: 'beam', key: newBeam.key })
      }
    } else if (mode === 'slab') {
      // Default flow: clicking inside a closed loop of beams fills that loop directly, so the
      // slab's edges line up with the beams that bound it without drawing a single vertex by
      // hand. Falls back to the old point-by-point polygon draw when the click doesn't land
      // inside any beam-bounded region (open structures, cantilevers, etc.).
      const face = findBeamFaceAt(beams, model)
      if (face) {
        const newSlab = createSlab(slabs, face)
        commitModel({ slabs: [...slabs, newSlab] })
        setSelected({ type: 'slab', key: newSlab.key })
        setMode('select')
      } else {
        setDrawingPolygon({ points: [{ x: sx, y: sy }], target: 'newSlab' })
        setMode('drawPolygon')
      }
    } else if (mode === 'areaLoad') {
      const slab = slabs.find((s) => pointInPolygon(model, s.polygon))
      if (slab) {
        const newLoad = createAreaLoad(loads, slab.id)
        commitModel({ loads: [...loads, newLoad] })
        setSelected({ type: 'load', key: newLoad.key })
      }
    } else if (mode === 'lineLoad') {
      let closest = null
      let closestDistance = 18 / scale
      beams.forEach((b) => {
        const a = modelToWorld(b.xI, b.yI)
        const c = modelToWorld(b.xJ, b.yJ)
        const distance = pointToSegmentDistance(pointer.x, pointer.y, a.x, a.y, c.x, c.y)
        if (distance < closestDistance) {
          closestDistance = distance
          closest = b
        }
      })
      if (closest) {
        const newLoad = createLineLoad(loads, closest.id)
        commitModel({ loads: [...loads, newLoad] })
        setSelected({ type: 'load', key: newLoad.key })
      }
    } else if (mode === 'pointLoad') {
      const newLoad = createPointLoad(loads, sx, sy)
      commitModel({ loads: [...loads, newLoad] })
      setSelected({ type: 'load', key: newLoad.key })
    }
  }

  function handleStageDblClick() {
    if (mode === 'drawPolygon') finishPolygonDraw()
  }

  // Mouse-down starts either a middle-button pan (any mode) or, in select mode on empty canvas, a
  // marquee selection. Picks on a shape (target !== stage) are left to their own click handlers.
  function handleStageMouseDown(e) {
    if (disabled) return
    suppressClickRef.current = false
    const stage = stageRef.current
    if (e.evt && e.evt.button === 1) {
      e.evt.preventDefault()
      const pointer = stage.getPointerPosition()
      panRef.current = { pointer, origin: { ...stagePos } }
      return
    }
    if (mode !== 'select') return
    if (e.target !== stage) return
    const p = stage.getRelativePointerPosition()
    marqueeStartRef.current = { x: p.x, y: p.y }
  }

  function handleStageMouseMove() {
    const stage = stageRef.current
    if (!stage) return

    if (panRef.current) {
      const pointer = stage.getPointerPosition()
      const { pointer: start, origin } = panRef.current
      setStagePos({ x: origin.x + (pointer.x - start.x), y: origin.y + (pointer.y - start.y) })
      return
    }

    if (marqueeStartRef.current) {
      const p = stage.getRelativePointerPosition()
      const start = marqueeStartRef.current
      setMarquee({ x0: start.x, y0: start.y, x1: p.x, y1: p.y, crossing: p.x < start.x })
    }
  }

  function handleStageMouseUp() {
    if (panRef.current) {
      panRef.current = null
      return
    }
    if (!marqueeStartRef.current) return
    const start = marqueeStartRef.current
    marqueeStartRef.current = null
    setMarquee(null)

    const stage = stageRef.current
    const end = stage?.getRelativePointerPosition()
    if (!end) return

    // A negligible drag is really a click — let the click handler pick a single item instead.
    const movedEnough = Math.hypot(end.x - start.x, end.y - start.y) > 3 / scale
    if (!movedEnough) return

    suppressClickRef.current = true
    const rect = normalizeRect(start.x, start.y, end.x, end.y)
    const crossing = end.x < start.x
    const picked = []

    pillars.forEach((p) => {
      const w = modelToWorld(p.x, p.y)
      if (pointInRect(rect, w.x, w.y)) picked.push({ type: 'pillar', key: p.key })
    })

    beams.forEach((b) => {
      const a = modelToWorld(b.xI, b.yI)
      const c = modelToWorld(b.xJ, b.yJ)
      const hit = crossing
        ? segmentIntersectsRect(rect, a, c)
        : pointInRect(rect, a.x, a.y) && pointInRect(rect, c.x, c.y)
      if (hit) picked.push({ type: 'beam', key: b.key })
    })

    slabs.forEach((s) => {
      const worldPoints = s.polygon.map((p) => modelToWorld(p.x, p.y))
      const hit = crossing ? polygonTouchesRect(rect, worldPoints) : polygonInsideRect(rect, worldPoints)
      if (hit) picked.push({ type: 'slab', key: s.key })
    })

    loads.forEach((l) => {
      let anchor = null
      if (l.type === 'point') {
        anchor = { x: l.x, y: l.y }
      } else if (l.type === 'line') {
        const beam = beams.find((b) => b.id === l.beam)
        if (beam) {
          const length = Math.hypot(beam.xJ - beam.xI, beam.yJ - beam.yI) || 1
          const lineStart = l.start === '' || l.start === undefined ? 0 : Number(l.start)
          const lineEnd = l.end === '' || l.end === undefined ? length : Number(l.end)
          const t = clamp((lineStart + lineEnd) / 2, 0, length) / length
          anchor = { x: beam.xI + (beam.xJ - beam.xI) * t, y: beam.yI + (beam.yJ - beam.yI) * t }
        }
      } else {
        const slab = slabs.find((s) => s.id === l.slab)
        const polygon = Array.isArray(l.polygon) && l.polygon.length >= 3 ? l.polygon : slab?.polygon
        if (polygon) anchor = polygonCentroid(polygon)
      }
      if (!anchor) return
      const w = modelToWorld(anchor.x, anchor.y)
      if (pointInRect(rect, w.x, w.y)) picked.push({ type: 'load', key: l.key })
    })

    setSelected(null)
    setNearbyCandidates([])
    setMultiSelection(picked)
  }

  const handleCanvasKeyRef = useRef()
  handleCanvasKeyRef.current = (e) => {
    if (disabled || showViewOptions) return false
    if (e.key === 'Escape') {
      const wasInFullscreen = isFullscreen
      let didCancel = false
      if (drawingPolygon) {
        cancelPolygonDraw()
        didCancel = true
      }
      if (pendingBeamPoint) {
        setPendingBeamPoint(null)
        didCancel = true
      }
      if (multiSelection.length > 0) {
        setMultiSelection([])
        didCancel = true
      }
      if (sectionElementIds) {
        setSectionAlignments([])
        didCancel = true
      }
      if (didCancel && wasInFullscreen) {
        setTimeout(() => wrapperRef.current?.requestFullscreen(), 0)
      }
      return didCancel
    }
    if (e.key === 'Enter' && drawingPolygon) {
      finishPolygonDraw()
      return true
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (multiSelection.length > 0) {
        deleteMultiSelection()
        return true
      }
      if (!selected) return false
      deleteSelected()
      return true
    }
    if (e.key === 'f' || e.key === 'F') {
      toggleFullscreen()
      return true
    }
    if (e.key === ' ') {
      selectMode('select')
      return true
    }
    return false
  }

  useEffect(() => {
    function handleKeyDown(e) {
      const target = e.target
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return
      }
      if (handleCanvasKeyRef.current(e)) e.preventDefault()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  function updateSelected(field, value) {
    if (!selected) return
    if (selected.type === 'pillar') {
      setPillars((prev) => prev.map((p) => (p.key === selected.key ? { ...p, [field]: value } : p)))
    } else if (selected.type === 'beam') {
      setBeams((prev) => prev.map((b) => (b.key === selected.key ? { ...b, [field]: value } : b)))
    } else if (selected.type === 'slab') {
      setSlabs((prev) => prev.map((s) => (s.key === selected.key ? { ...s, [field]: value } : s)))
    } else if (selected.type === 'load') {
      setLoads((prev) => prev.map((l) => (l.key === selected.key ? { ...l, [field]: value } : l)))
    }
  }

  function updateSlabBoundaryEdge(slabKey, edgeKey, field, value) {
    setSlabs((prev) =>
      prev.map((s) => {
        if (s.key !== slabKey) return s
        const boundaryEdges = (s.boundaryEdges || []).map((e) => (e.key === edgeKey ? { ...e, [field]: value } : e))
        return { ...s, boundaryEdges }
      })
    )
  }

  function addSlabBoundaryEdge(slabKey, edge) {
    setSlabs((prev) =>
      prev.map((s) => {
        if (s.key !== slabKey) return s
        const existingEdges = s.boundaryEdges || []
        const newEdge = createBoundaryEdge(existingEdges, edge)
        return { ...s, boundaryEdges: [...existingEdges, newEdge] }
      })
    )
  }

  function selectCandidate(candidate) {
    if (!candidate) {
      setSelected(null)
      return
    }
    if (candidate.type === 'slabEdge') {
      const slab = slabs.find((s) => s.key === candidate.slabKey)
      const exists = slab?.boundaryEdges?.some((e) => Number(e.edge) === candidate.edgeIndex)
      if (!exists) addSlabBoundaryEdge(candidate.slabKey, candidate.edgeIndex)
    }
    setSelected(candidate)
  }

  function removeSlabBoundaryEdge(slabKey, edgeKey) {
    setSlabs((prev) =>
      prev.map((s) => {
        if (s.key !== slabKey) return s
        return { ...s, boundaryEdges: (s.boundaryEdges || []).filter((e) => e.key !== edgeKey) }
      })
    )
  }

  function updateSlabVertex(slabKey, index, x, y) {
    setSlabs((prev) =>
      prev.map((s) => {
        if (s.key !== slabKey) return s
        const polygon = s.polygon.map((p, i) => (i === index ? { x, y } : p))
        return { ...s, polygon }
      })
    )
  }

  function deleteSelected() {
    if (!selected) return
    if (selected.type === 'pillar') {
      commitModel({ pillars: pillars.filter((p) => p.key !== selected.key) })
    } else if (selected.type === 'beam') {
      const res = cascadeRemoveBeam(selected.key, { beams, loads })
      commitModel({ beams: res.beams, loads: res.loads })
    } else if (selected.type === 'slab') {
      const res = cascadeRemoveSlab(selected.key, { slabs, loads })
      commitModel({ slabs: res.slabs, loads: res.loads })
    } else if (selected.type === 'load') {
      commitModel({ loads: loads.filter((l) => l.key !== selected.key) })
    }
    setSelected(null)
    setNearbyCandidates([])
  }

  // Deletes every pillar/beam/slab/load in a marquee selection in one pass. Removing a beam or
  // slab also drops the line/area loads that reference it (matching cascadeRemoveBeam/Slab).
  function deleteMultiSelection() {
    if (multiSelection.length === 0) return
    const pillarKeys = new Set(multiSelection.filter((s) => s.type === 'pillar').map((s) => s.key))
    const beamKeys = new Set(multiSelection.filter((s) => s.type === 'beam').map((s) => s.key))
    const slabKeys = new Set(multiSelection.filter((s) => s.type === 'slab').map((s) => s.key))
    const loadKeys = new Set(multiSelection.filter((s) => s.type === 'load').map((s) => s.key))

    const removedBeamIds = new Set(beams.filter((b) => beamKeys.has(b.key)).map((b) => b.id))
    const removedSlabIds = new Set(slabs.filter((s) => slabKeys.has(s.key)).map((s) => s.id))

    const newLoads = loads.filter((l) => {
      if (loadKeys.has(l.key)) return false
      if (l.type === 'line' && removedBeamIds.has(l.beam)) return false
      if (l.type === 'area' && removedSlabIds.has(l.slab)) return false
      return true
    })

    commitModel({
      pillars: pillars.filter((p) => !pillarKeys.has(p.key)),
      beams: beams.filter((b) => !beamKeys.has(b.key)),
      slabs: slabs.filter((s) => !slabKeys.has(s.key)),
      loads: newLoads,
    })
    setMultiSelection([])
    setSelected(null)
    setNearbyCandidates([])
  }

  const gridLines = useMemo(() => {
    const lines = []
    const extent = GRID_EXTENT * BASE_SCALE
    const stepX = Number(gridSpacingX) > 0 ? Number(gridSpacingX) : DEFAULT_GRID_SPACING
    const stepY = Number(gridSpacingY) > 0 ? Number(gridSpacingY) : DEFAULT_GRID_SPACING
    const countX = Math.min(Math.floor(GRID_EXTENT / stepX), MAX_GRID_LINES)
    const countY = Math.min(Math.floor(GRID_EXTENT / stepY), MAX_GRID_LINES)
    for (let n = -countX; n <= countX; n += 1) {
      const x = n * stepX * BASE_SCALE
      lines.push({ key: `v${n}`, points: [x, -extent, x, extent], axis: n === 0 })
    }
    for (let n = -countY; n <= countY; n += 1) {
      const y = n * stepY * BASE_SCALE
      lines.push({ key: `h${n}`, points: [-extent, y, extent, y], axis: n === 0 })
    }
    return lines
  }, [gridSpacingX, gridSpacingY])

  const slabColor = theme.mode === 'dark' ? 'rgba(94,234,212,0.14)' : 'rgba(13,148,136,0.10)'
  const slabStroke = theme.mode === 'dark' ? '#5eead4' : '#0d9488'
  const beamColor = theme.mode === 'dark' ? '#c2b280' : '#8a7d3a'
  const pillarColor = theme.mode === 'dark' ? '#f59e0b' : '#b45309'
  const gridColor = theme.mode === 'dark' ? '#1f2a3d' : '#e2e8f0'
  const axisColor = theme.mode === 'dark' ? '#334155' : '#cbd5e1'
  const selectionColor = theme.colors.primary
  const loadColor = theme.mode === 'dark' ? '#f87171' : '#dc2626'
  const reactionColor = theme.mode === 'dark' ? '#34d399' : '#16a34a'
  const displacementColor = theme.mode === 'dark' ? '#e879f9' : '#c026d3'
  const meshColor = theme.mode === 'dark' ? '#64748b' : '#94a3b8'
  const loadsVisible = showLoads || mode === 'areaLoad' || mode === 'lineLoad' || mode === 'pointLoad'
  // AutoCAD-style marquee colors: blue window (left-to-right, fully enclosed) vs. green crossing
  // (right-to-left, anything touched).
  const marqueeWindowColor = theme.mode === 'dark' ? '#60a5fa' : '#2563eb'
  const marqueeCrossingColor = theme.mode === 'dark' ? '#4ade80' : '#16a34a'

  // O(1) membership lookups for highlighting the marquee's multi-selection.
  const selectedPillarKeys = useMemo(
    () => new Set(multiSelection.filter((s) => s.type === 'pillar').map((s) => s.key)),
    [multiSelection]
  )
  const selectedBeamKeys = useMemo(
    () => new Set(multiSelection.filter((s) => s.type === 'beam').map((s) => s.key)),
    [multiSelection]
  )
  const selectedSlabKeys = useMemo(
    () => new Set(multiSelection.filter((s) => s.type === 'slab').map((s) => s.key)),
    [multiSelection]
  )
  const selectedLoadKeys = useMemo(
    () => new Set(multiSelection.filter((s) => s.type === 'load').map((s) => s.key)),
    [multiSelection]
  )

  const k = 1 / scale
  const textScaleF = positiveScale(viewScales.text)
  const diagramScaleF = positiveScale(viewScales.diagram)
  const nodeScaleF = positiveScale(viewScales.node)

  return (
    <CanvasWrapper ref={wrapperRef} $isFullscreen={isFullscreen}>
      <Toolbar>
        <ToolButton
          type="button"
          $active={mode === 'select'}
          onClick={() => selectMode('select')}
          disabled={disabled || isometricView}
        >
          <FiMousePointer /> Selecionar
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'pillar'}
          onClick={() => selectMode('pillar')}
          disabled={disabled || isometricView}
        >
          <FiSquare /> Pilar
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'beam'}
          onClick={() => selectMode('beam')}
          disabled={disabled || isometricView}
        >
          <FiMinus /> Viga
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'slab'}
          onClick={() => selectMode('slab')}
          disabled={disabled || isometricView}
        >
          <FiLayers /> Laje
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'areaLoad'}
          onClick={() => selectMode('areaLoad')}
          disabled={disabled || isometricView}
        >
          <FiGrid /> Carga de Área
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'lineLoad'}
          onClick={() => selectMode('lineLoad')}
          disabled={disabled || isometricView}
        >
          <FiTrendingDown /> Carga Linear
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'pointLoad'}
          onClick={() => selectMode('pointLoad')}
          disabled={disabled || isometricView}
        >
          <FiArrowDown /> Carga Pontual
        </ToolButton>
        {drawingPolygon && (
          <>
            <ToolButton type="button" onClick={finishPolygonDraw} disabled={drawingPolygon.points.length < 3}>
              <FiCheck /> Concluir
            </ToolButton>
            <ToolButton type="button" onClick={cancelPolygonDraw}>
              <FiX /> Cancelar
            </ToolButton>
          </>
        )}
        <ToolbarSeparator />
        <PropertyRow style={{ marginBottom: 0 }}>
          Malha (m)
          <input
            type="number"
            min={0.1}
            step={0.1}
            value={meshMaxSpacing}
            onChange={(e) => setMeshMaxSpacing(e.target.value)}
            disabled={disabled}
            style={{ width: '70px' }}
          />
        </PropertyRow>
        <ToolbarSeparator />
        <ToolButton type="button" onClick={() => zoomBy(1.2)} disabled={disabled} title="Aproximar">
          <FiZoomIn />
        </ToolButton>
        <ToolButton type="button" onClick={() => zoomBy(1 / 1.2)} disabled={disabled} title="Afastar">
          <FiZoomOut />
        </ToolButton>
        <ToolButton type="button" onClick={resetView} disabled={disabled} title="Restaurar vista">
          <FiMaximize2 />
        </ToolButton>
        <ToolButton type="button" onClick={toggleFullscreen} title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}>
          {isFullscreen ? <FiMinimize /> : <FiMaximize />}
        </ToolButton>
        <ToolbarSeparator />
        <ToolButton
          type="button"
          $active={showPillars}
          onClick={() => setShowPillars((prev) => !prev)}
          title="Mostrar/ocultar pilares"
        >
          Pilares
        </ToolButton>
        <ToolButton
          type="button"
          $active={showBeams}
          onClick={() => setShowBeams((prev) => !prev)}
          title="Mostrar/ocultar vigas"
        >
          Vigas
        </ToolButton>
        <ToolButton
          type="button"
          $active={showSlabs}
          onClick={() => setShowSlabs((prev) => !prev)}
          title="Mostrar/ocultar lajes"
        >
          Lajes
        </ToolButton>
        <ToolButton
          type="button"
          $active={showBoundaryEdges}
          onClick={() => setShowBoundaryEdges((prev) => !prev)}
          title="Mostrar/ocultar bordos de laje"
        >
          Bordos
        </ToolButton>
        <ToolbarSeparator />
        <ToolButton type="button" onClick={() => setShowViewOptions(true)} title="Snap, rótulos e escalas">
          <FiSliders /> Opções de visualização
        </ToolButton>
        {isFullscreen && <ToolbarBreak />}
        {hasResult && (
          <>
            {!isFullscreen && <ToolbarSeparator />}
            <ToolButton type="button" $active={showMesh} onClick={() => setShowMesh((prev) => !prev)}>
              Malha gerada
            </ToolButton>
            <ToolButton type="button" $active={showReactions} onClick={() => setShowReactions((prev) => !prev)}>
              <FiActivity /> Reações
            </ToolButton>
            <ToolButton
              type="button"
              $active={showDisplacements}
              disabled={isometricView}
              onClick={() => setShowDisplacements((prev) => !prev)}
            >
              <FiMove /> Deformada
            </ToolButton>
            <ToolButton
              type="button"
              $active={isometricView}
              onClick={() => {
                setIsometricView((prev) => !prev)
                selectMode('select')
              }}
              title="Mostra só a forma deformada, projetada em vista isométrica (substitui a vista em planta)"
            >
              <FiBox /> Vista Isométrica
            </ToolButton>
            <ToolButton
              type="button"
              $active={diagramMode === 'none'}
              disabled={isometricView}
              onClick={() => {
                setDiagramMode('none')
                setQueryMarker(null)
              }}
            >
              Sem diagrama
            </ToolButton>
            <ToolButton
              type="button"
              $active={diagramMode === 'V'}
              disabled={isometricView}
              onClick={() => setDiagramMode('V')}
            >
              Cortante (V)
            </ToolButton>
            <ToolButton
              type="button"
              $active={diagramMode === 'M'}
              disabled={isometricView}
              onClick={() => setDiagramMode('M')}
            >
              Momento (M)
            </ToolButton>
            <ToolButton
              type="button"
              $active={diagramMode === 'T'}
              disabled={isometricView}
              onClick={() => setDiagramMode('T')}
            >
              Torção (T)
            </ToolButton>
            <ToolButton
              type="button"
              $active={diagramMode === 'w'}
              disabled={isometricView}
              onClick={() => setDiagramMode('w')}
              title="Isovalores coloridos do deslocamento (w) sobre a malha"
            >
              Deslocamento (w)
            </ToolButton>
            {diagramMode !== 'none' && diagramMode !== 'w' && (
              <ToolButton
                type="button"
                $active={perMeter}
                onClick={() => setPerMeter((prev) => !prev)}
                title="Divide V/M/T pela distância até as linhas paralelas vizinhas na malha (largura tributária estimada)"
              >
                Por metro
              </ToolButton>
            )}
            {diagramMode !== 'none' && sectionElementIds && (
              <span
                style={{ fontSize: '12px', opacity: 0.75 }}
                title="Shift+clique une outra barra alinhada ao corte. Clique em área vazia para limpar o corte."
              >
                {sectionElementIds.length} barra{sectionElementIds.length > 1 ? 's' : ''} no corte
                {sectionAlignments.length > 1 ? ` (${sectionAlignments.length} trechos)` : ''}
              </span>
            )}
            {diagramMode !== 'none' &&
              displayDiagramInfo &&
              (() => {
                const range = displayDiagramInfo.range[diagramMode]
                if (!range || !Number.isFinite(range.min) || !Number.isFinite(range.max)) return null
                const isDisplacement = diagramMode === 'w'
                const unit = isDisplacement ? ' cm' : perMeter ? '/m' : ''
                const displayValue = (v) => roundToDecimals(isDisplacement ? v * 100 : v, 2)
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <span>
                      {displayValue(range.min)}
                      {unit}
                    </span>
                    <div
                      style={{
                        width: '90px',
                        height: '10px',
                        borderRadius: '4px',
                        background:
                          'linear-gradient(to right, hsl(214,82%,33%), hsl(214,82%,78%), hsl(210,15%,78%), hsl(6,82%,78%), hsl(6,82%,33%))',
                      }}
                    />
                    <span>
                      {displayValue(range.max)}
                      {unit}
                    </span>
                  </div>
                )
              })()}
          </>
        )}
        <ToolbarSeparator />
        <ToolButton type="button" onClick={onSave} disabled={disabled}>
          <FiSave /> Salvar
        </ToolButton>
        <ToolButton type="button" onClick={() => fileInputRef.current?.click()} disabled={disabled}>
          <FiUpload /> Carregar
        </ToolButton>
        <ToolButton type="button" $active onClick={onCalculate} disabled={!isFormValid || disabled || !canCalculate}>
          {disabled ? (
            <>
              <Spinner size="14px" /> Calculando...
            </>
          ) : !canCalculate ? (
            'Limite atingido'
          ) : (
            <>
              <FiPlay /> Calcular
            </>
          )}
        </ToolButton>
        <input type="file" ref={fileInputRef} onChange={onFileUpload} style={{ display: 'none' }} />
      </Toolbar>
      <HintBar>
        {isometricView
          ? 'Vista isométrica somente leitura: mostra a forma deformada amplificada em 3D. Role para zoom, arraste para deslocar a vista. Clique em "Vista Isométrica" novamente para voltar à planta.'
          : HINTS[mode]}
      </HintBar>
      {formErrors?.length > 0 && (
        <FormErrorsBox>
          <strong>Corrija antes de calcular:</strong>
          <ul>
            {formErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </FormErrorsBox>
      )}
      <CanvasBody $isFullscreen={isFullscreen}>
        <StageHost
          ref={hostRef}
          $cursor={mode === 'select' ? 'default' : 'crosshair'}
          style={{ height: isFullscreen ? '100%' : `${STAGE_HEIGHT}px` }}
        >
          <Stage
            ref={stageRef}
            width={stageWidth}
            height={stageHeight}
            scaleX={scale}
            scaleY={scale}
            x={stagePos.x}
            y={stagePos.y}
            draggable={!disabled && mode !== 'select' && mode !== 'drawPolygon'}
            onDragEnd={(e) => {
              if (e.target === e.target.getStage()) setStagePos({ x: e.target.x(), y: e.target.y() })
            }}
            onWheel={handleWheel}
            onMouseDown={handleStageMouseDown}
            onMouseMove={handleStageMouseMove}
            onMouseUp={handleStageMouseUp}
            onMouseLeave={handleStageMouseUp}
            onClick={handleStageClick}
            onTap={handleStageClick}
            onDblClick={handleStageDblClick}
          >
            <Layer listening={false}>
              {!isometricView &&
                gridLines.map((line) => (
                  <Line
                    key={line.key}
                    points={line.points}
                    stroke={line.axis ? axisColor : gridColor}
                    strokeWidth={(line.axis ? 1.5 : 1) * k}
                  />
                ))}
            </Layer>

            <Layer>
              {isometricView ? (
                <>
                  {hasResult &&
                    isometricDeformedShape &&
                    isometricDeformedShape.polylines.map((pl) => (
                      <Line
                        key={`iso-deformed-${pl.id}`}
                        points={pl.points}
                        stroke={displacementColor}
                        strokeWidth={2 * k}
                        listening={false}
                      />
                    ))}
                </>
              ) : (
                <>
                  {showSlabs &&
                    slabs.map((s) => {
                      const isSelected =
                        (selected?.type === 'slab' && selected.key === s.key) || selectedSlabKeys.has(s.key)
                      const points = s.polygon.flatMap((p) => {
                        const w = modelToWorld(p.x, p.y)
                        return [w.x, w.y]
                      })
                      const centroidModel = polygonCentroid(s.polygon)
                      const centroid = modelToWorld(centroidModel.x, centroidModel.y)
                      return (
                        <Group key={s.key}>
                          <Line
                            points={points}
                            closed
                            fill={slabColor}
                            stroke={isSelected ? selectionColor : slabStroke}
                            strokeWidth={(isSelected ? 3 : 1.5) * k}
                          />
                          {showLabels && (
                            <Text
                              x={centroid.x - 10 * k}
                              y={centroid.y - 6 * k}
                              text={`${s.id} (h=${s.thickness})`}
                              fontSize={12 * k * textScaleF}
                              fill={slabStroke}
                              listening={false}
                            />
                          )}
                          {isSelected &&
                            s.polygon.map((p, i) => {
                              const w = modelToWorld(p.x, p.y)
                              return (
                                <Circle
                                  key={i}
                                  x={w.x}
                                  y={w.y}
                                  radius={6 * k}
                                  fill={selectionColor}
                                  draggable={!disabled}
                                  onDragEnd={(e) => {
                                    const pos = e.target.position()
                                    const m = worldToModel(pos.x, pos.y)
                                    updateSlabVertex(s.key, i, snap(m.x, snapSize), snap(m.y, snapSize))
                                  }}
                                />
                              )
                            })}
                        </Group>
                      )
                    })}

                  {showBoundaryEdges &&
                    showSlabs &&
                    slabs.map((s) => (
                      <Group key={`edges-${s.key}`}>
                        {s.polygon.map((p, edgeIdx) => {
                          const nextIdx = (edgeIdx + 1) % s.polygon.length
                          const nextP = s.polygon[nextIdx]
                          const isConfigured = s.boundaryEdges?.some((e) => Number(e.edge) === edgeIdx)
                          const w1 = modelToWorld(p.x, p.y)
                          const w2 = modelToWorld(nextP.x, nextP.y)
                          const edgeConfig = s.boundaryEdges?.find((e) => Number(e.edge) === edgeIdx)
                          return (
                            <Group
                              key={`edge-${s.key}-${edgeIdx}`}
                              onClick={(e) => {
                                e.cancelBubble = true
                                selectCandidate({ type: 'slabEdge', slabKey: s.key, edgeIndex: edgeIdx })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <Line
                                points={[w1.x, w1.y, w2.x, w2.y]}
                                stroke={edgeConfig?.releaseMoment ? '#f59e0b' : isConfigured ? '#8b5cf6' : '#cbd5e1'}
                                strokeWidth={edgeConfig?.releaseMoment ? 4 * k : isConfigured ? 3 * k : 1.5 * k}
                                hitStrokeWidth={16 * k}
                                listening
                              />
                              {showLabels && isConfigured && (
                                <Text
                                  x={(w1.x + w2.x) / 2 - 10 * k}
                                  y={(w1.y + w2.y) / 2 - 12 * k}
                                  text={edgeConfig?.id || `E${edgeIdx}`}
                                  fontSize={10 * k * textScaleF}
                                  fill={edgeConfig?.releaseMoment ? '#f59e0b' : '#8b5cf6'}
                                  listening={false}
                                />
                              )}
                            </Group>
                          )
                        })}
                      </Group>
                    ))}

                  {showBeams &&
                    beams.map((b) => {
                      const a = modelToWorld(b.xI, b.yI)
                      const c = modelToWorld(b.xJ, b.yJ)
                      const isSelected =
                        (selected?.type === 'beam' && selected.key === b.key) || selectedBeamKeys.has(b.key)
                      return (
                        <Group key={b.key}>
                          <Line
                            points={[a.x, a.y, c.x, c.y]}
                            stroke={isSelected ? selectionColor : beamColor}
                            strokeWidth={(isSelected ? 5 : 4) * k}
                            hitStrokeWidth={16 * k}
                          />
                          {showLabels && (
                            <Text
                              x={(a.x + c.x) / 2 + 6 * k}
                              y={(a.y + c.y) / 2 + 6 * k}
                              text={`${b.id} (${b.width}x${b.height})`}
                              fontSize={12 * k * textScaleF}
                              fill={theme.colors.textSecondary}
                              listening={false}
                            />
                          )}
                          {isSelected && (
                            <>
                              <Circle
                                x={a.x}
                                y={a.y}
                                radius={6 * k}
                                fill={selectionColor}
                                draggable={!disabled}
                                onDragEnd={(e) => {
                                  const pos = e.target.position()
                                  const m = worldToModel(pos.x, pos.y)
                                  updateSelected('xI', snap(m.x, snapSize))
                                  updateSelected('yI', snap(m.y, snapSize))
                                }}
                              />
                              <Circle
                                x={c.x}
                                y={c.y}
                                radius={6 * k}
                                fill={selectionColor}
                                draggable={!disabled}
                                onDragEnd={(e) => {
                                  const pos = e.target.position()
                                  const m = worldToModel(pos.x, pos.y)
                                  updateSelected('xJ', snap(m.x, snapSize))
                                  updateSelected('yJ', snap(m.y, snapSize))
                                }}
                              />
                            </>
                          )}
                        </Group>
                      )
                    })}

                  {showPillars &&
                    pillars.map((p) => {
                      const w = modelToWorld(p.x, p.y)
                      const halfW = (Number(p.width) || 0.3) * 0.5 * BASE_SCALE
                      const halfD = (Number(p.depth) || 0.3) * 0.5 * BASE_SCALE
                      const isSelected =
                        (selected?.type === 'pillar' && selected.key === p.key) || selectedPillarKeys.has(p.key)
                      return (
                        <Group key={p.key}>
                          <Rect
                            x={w.x - halfW}
                            y={w.y - halfD}
                            width={halfW * 2}
                            height={halfD * 2}
                            fill={isSelected ? selectionColor : pillarColor}
                            draggable={!disabled}
                            onDragEnd={(e) => {
                              const pos = e.target.position()
                              const center = { x: pos.x + halfW, y: pos.y + halfD }
                              const m = worldToModel(center.x, center.y)
                              updateSelected('x', snap(m.x, snapSize))
                              updateSelected('y', snap(m.y, snapSize))
                            }}
                          />
                          {showLabels && (
                            <Text
                              x={w.x + halfW + 4 * k}
                              y={w.y - 6 * k}
                              text={p.id}
                              fontSize={12 * k * textScaleF}
                              fontStyle="bold"
                              fill={pillarColor}
                              listening={false}
                            />
                          )}
                        </Group>
                      )
                    })}

                  {loadsVisible &&
                    loads.map((l) => {
                      const isSelected =
                        (selected?.type === 'load' && selected.key === l.key) || selectedLoadKeys.has(l.key)
                      const color = isSelected ? selectionColor : loadColor

                      if (l.type === 'area') {
                        const slab = slabs.find((s) => s.id === l.slab)
                        const polygon = Array.isArray(l.polygon) && l.polygon.length >= 3 ? l.polygon : slab?.polygon
                        if (!polygon) return null
                        const centroidModel = polygonCentroid(polygon)
                        const centroid = modelToWorld(centroidModel.x, centroidModel.y)
                        const points = polygon.flatMap((p) => {
                          const w = modelToWorld(p.x, p.y)
                          return [w.x, w.y]
                        })
                        return (
                          <Group key={l.key}>
                            <Line points={points} closed fill={`${color}22`} stroke={color} strokeWidth={1.5 * k} />
                            <Text
                              x={centroid.x - 10 * k}
                              y={centroid.y + 8 * k}
                              text={`${l.id}: ${l.value}`}
                              fontSize={11 * k * textScaleF}
                              fill={color}
                              listening={false}
                            />
                          </Group>
                        )
                      }

                      if (l.type === 'line') {
                        const beam = beams.find((b) => b.id === l.beam)
                        if (!beam) return null
                        const length = Math.hypot(beam.xJ - beam.xI, beam.yJ - beam.yI) || 1
                        const start = clamp(l.start === '' || l.start === undefined ? 0 : Number(l.start), 0, length)
                        const end = clamp(l.end === '' || l.end === undefined ? length : Number(l.end), 0, length)
                        const screenDir = { x: 0, y: Number(l.value) > 0 ? -1 : 1 }
                        const arrowCount = 5
                        const arrows = []
                        for (let i = 0; i < arrowCount; i += 1) {
                          const t = arrowCount === 1 ? 0.5 : i / (arrowCount - 1)
                          const pos = (start + (end - start) * t) / length
                          const px = beam.xI + (beam.xJ - beam.xI) * pos
                          const py = beam.yI + (beam.yJ - beam.yI) * pos
                          const tip = modelToWorld(px, py)
                          const tail = { x: tip.x - screenDir.x * 24 * k, y: tip.y - screenDir.y * 24 * k }
                          arrows.push(
                            <Arrow
                              key={i}
                              points={[tail.x, tail.y, tip.x, tip.y]}
                              stroke={color}
                              fill={color}
                              strokeWidth={2 * k}
                              pointerLength={6 * k}
                              pointerWidth={6 * k}
                              hitStrokeWidth={14 * k}
                            />
                          )
                        }
                        const midT = (start + end) / 2 / length
                        const midTip = modelToWorld(
                          beam.xI + (beam.xJ - beam.xI) * midT,
                          beam.yI + (beam.yJ - beam.yI) * midT
                        )
                        return (
                          <Group key={l.key}>
                            {arrows}
                            <Text
                              x={midTip.x - screenDir.x * 38 * k}
                              y={midTip.y - screenDir.y * 38 * k - 14 * k}
                              text={`${l.id}: ${l.value}`}
                              fontSize={11 * k * textScaleF}
                              fill={color}
                              listening={false}
                            />
                          </Group>
                        )
                      }

                      const tip = modelToWorld(l.x, l.y)
                      const screenDir = { x: 0, y: Number(l.value) > 0 ? -1 : 1 }
                      const tail = { x: tip.x - screenDir.x * 40 * k, y: tip.y - screenDir.y * 40 * k }
                      const momentLabel = []
                      if (Number(l.Mx)) momentLabel.push(`Mx=${l.Mx}`)
                      if (Number(l.My)) momentLabel.push(`My=${l.My}`)
                      return (
                        <Group key={l.key}>
                          <Circle
                            x={tip.x}
                            y={tip.y}
                            radius={5 * k}
                            fill={color}
                            draggable={!disabled}
                            onDragEnd={(e) => {
                              const pos = e.target.position()
                              const m = worldToModel(pos.x, pos.y)
                              updateSelected('x', snap(m.x, snapSize))
                              updateSelected('y', snap(m.y, snapSize))
                            }}
                          />
                          <Arrow
                            points={[tail.x, tail.y, tip.x, tip.y]}
                            stroke={color}
                            fill={color}
                            strokeWidth={2.5 * k}
                            pointerLength={8 * k}
                            pointerWidth={8 * k}
                            listening={false}
                          />
                          <Text
                            x={tail.x - 10 * k}
                            y={tail.y - 16 * k}
                            text={`${l.id}: ${l.value}${momentLabel.length ? ` ${momentLabel.join(' ')}` : ''}`}
                            fontSize={11 * k * textScaleF}
                            fill={color}
                            listening={false}
                          />
                        </Group>
                      )
                    })}

                  {drawingPolygon && (
                    <Group listening={false}>
                      <Line
                        points={drawingPolygon.points.flatMap((p) => {
                          const w = modelToWorld(p.x, p.y)
                          return [w.x, w.y]
                        })}
                        stroke={selectionColor}
                        strokeWidth={2 * k}
                        dash={[8 * k, 5 * k]}
                      />
                      {drawingPolygon.points.map((p, i) => {
                        const w = modelToWorld(p.x, p.y)
                        return <Circle key={i} x={w.x} y={w.y} radius={4 * k} fill={selectionColor} />
                      })}
                    </Group>
                  )}

                  {pendingBeamPoint &&
                    (() => {
                      const w = modelToWorld(pendingBeamPoint.x, pendingBeamPoint.y)
                      return <Circle x={w.x} y={w.y} radius={6 * k} fill="#f59e0b" listening={false} />
                    })()}

                  {showMesh &&
                    hasResult &&
                    (result.model.elements || []).map((el) => {
                      const nodeI = modelNodesById[el.nodeI]
                      const nodeJ = modelNodesById[el.nodeJ]
                      if (!nodeI || !nodeJ) return null
                      const a = modelToWorld(nodeI.x, nodeI.y)
                      const c = modelToWorld(nodeJ.x, nodeJ.y)
                      return (
                        <Line
                          key={`mesh-el-${el.id}`}
                          points={[a.x, a.y, c.x, c.y]}
                          stroke={meshColor}
                          strokeWidth={1 * k}
                          listening={false}
                        />
                      )
                    })}

                  {showMesh &&
                    hasResult &&
                    (result.model.nodes || []).map((n) => {
                      const w = modelToWorld(n.x, n.y)
                      return (
                        <Circle
                          key={`mesh-node-${n.id}`}
                          x={w.x}
                          y={w.y}
                          radius={2.5 * k * nodeScaleF}
                          fill={meshColor}
                        />
                      )
                    })}

                  {/* Without a section isolated: a fine mesh has dozens of tiny elements crossing at every
                  angle, so each one is drawn as a single line colored by its own value on a diverging
                  blue↔red scale (see diagramValueColor) — a heatmap over the mesh wireframe. Clicking
                  any element isolates its whole line of collinear bars (computeMeshAlignments); once a
                  section is isolated, the actual V/M/T curve moves to a standalone chart in the
                  properties panel (away from the geometry) and the canvas just highlights which line
                  it belongs to, so every element still needs to stay clickable to isolate a different
                  line, but only the selected one is drawn in the highlight color. */}
                  {diagramMode !== 'none' &&
                    displayDiagramInfo &&
                    (result?.model?.elements || []).map((el) => {
                      const nodeI = modelNodesById[el.nodeI]
                      const nodeJ = modelNodesById[el.nodeJ]
                      const diagram = displayDiagramInfo.diagramsByElementId[el.id]
                      if (!nodeI || !nodeJ || !diagram) return null

                      const inSection = sectionElementIds?.includes(el.id)
                      let color
                      let strokeWidth
                      if (!sectionElementIds) {
                        const range = displayDiagramInfo.range[diagramMode]
                        const midIndex = Math.floor((diagram.length - 1) / 2)
                        const value = Number(diagram[midIndex]?.[diagramMode]) || 0
                        color = diagramValueColor(value, range)
                        strokeWidth = (4 + 3 * diagramScaleF) * k
                      } else if (inSection) {
                        color = selectionColor
                        strokeWidth = (5 + 3 * diagramScaleF) * k
                      } else {
                        color = meshColor
                        strokeWidth = 1 * k
                      }
                      const a = modelToWorld(nodeI.x, nodeI.y)
                      const c = modelToWorld(nodeJ.x, nodeJ.y)

                      function handleSegmentClick(e) {
                        e.cancelBubble = true
                        const stage = e.target.getStage()
                        const pointer = stage.getRelativePointerPosition()
                        const model = worldToModel(pointer.x, pointer.y)
                        const { ux, uy, length } = elementVectors(nodeI, nodeJ)
                        const x = clamp((model.x - nodeI.x) * ux + (model.y - nodeI.y) * uy, 0, length)
                        const xs = diagram.map((p) => Number(p.x))
                        const values = diagram.map((p) => Number(p[diagramMode]) || 0)
                        const pointValue = interpolateAt(xs, values, x)
                        const t = x / length
                        const px = nodeI.x + (nodeJ.x - nodeI.x) * t
                        const py = nodeI.y + (nodeJ.y - nodeI.y) * t
                        const horizontal = Math.abs(nodeJ.y - nodeI.y) <= Math.abs(nodeJ.x - nodeI.x)
                        setQueryMarker({
                          elementId: el.id,
                          x,
                          value: pointValue,
                          pos: modelToWorld(px, py),
                          horizontal,
                        })
                        const clickedAlignment = meshAlignments.find((al) => al.elementIds.includes(el.id))
                        if (!clickedAlignment) {
                          setSectionAlignments([])
                          return
                        }
                        const isSameAlignment = (a, b) =>
                          a.elementIds.length === b.elementIds.length &&
                          a.elementIds.every((id) => b.elementIds.includes(id))
                        if (e.evt?.shiftKey && sectionAlignments.length > 0) {
                          const alreadySelected = sectionAlignments.some((a) => isSameAlignment(a, clickedAlignment))
                          if (alreadySelected) {
                            setSectionAlignments(sectionAlignments.filter((a) => !isSameAlignment(a, clickedAlignment)))
                            return
                          }
                          const reference = sectionAlignments[0]
                          const collinear =
                            isParallelDir(reference.dir, clickedAlignment.dir) &&
                            Math.abs(reference.offset - clickedAlignment.offset) < 1e-6
                          setSectionAlignments(
                            collinear ? [...sectionAlignments, clickedAlignment] : [clickedAlignment]
                          )
                        } else {
                          setSectionAlignments([clickedAlignment])
                        }
                      }

                      return (
                        <Line
                          key={`diagram-${el.id}`}
                          points={[a.x, a.y, c.x, c.y]}
                          stroke={color}
                          strokeWidth={strokeWidth}
                          hitStrokeWidth={14 * k}
                          onClick={handleSegmentClick}
                          onTap={handleSegmentClick}
                        />
                      )
                    })}

                  {diagramMode !== 'none' &&
                    queryMarker &&
                    diagramInfo?.diagramsByElementId?.[queryMarker.elementId] &&
                    (() => {
                      const labelText = `x=${roundToDecimals(queryMarker.x, 2)}: ${
                        diagramMode === 'w'
                          ? `${roundToDecimals(queryMarker.value * 100, 2)} cm`
                          : `${roundToDecimals(queryMarker.value, 2)}${perMeter ? '/m' : ''}`
                      }`
                      const labelMargin = 22 * k
                      const labelBoxW = 150 * k
                      const fontSize = 11 * k * textScaleF
                      // Parks the label clear of the mesh (above it for horizontal bars, to its
                      // left for vertical ones) instead of right on top of the diagram line it's
                      // reading from, with a dashed leader connecting it back to the picked point.
                      let labelX = queryMarker.pos.x + 6 * k
                      let labelY = queryMarker.pos.y + 6 * k
                      let align = 'left'
                      let anchor = { x: labelX, y: labelY }
                      if (structureWorldBounds) {
                        if (queryMarker.horizontal) {
                          anchor = { x: queryMarker.pos.x, y: structureWorldBounds.minY - labelMargin }
                          labelX = anchor.x - labelBoxW / 2
                          labelY = anchor.y - fontSize - 4 * k
                          align = 'center'
                        } else {
                          anchor = { x: structureWorldBounds.minX - labelMargin, y: queryMarker.pos.y }
                          labelX = anchor.x - labelBoxW
                          labelY = anchor.y - fontSize / 2
                          align = 'right'
                        }
                      }
                      return (
                        <Group listening={false}>
                          <Circle
                            x={queryMarker.pos.x}
                            y={queryMarker.pos.y}
                            radius={5 * k}
                            stroke={theme.colors.primary}
                            strokeWidth={2 * k}
                            fill={theme.colors.background}
                          />
                          {structureWorldBounds && (
                            <Line
                              points={[queryMarker.pos.x, queryMarker.pos.y, anchor.x, anchor.y]}
                              stroke={theme.colors.primary}
                              strokeWidth={1 * k}
                              dash={[4 * k, 3 * k]}
                              opacity={0.6}
                            />
                          )}
                          <Text
                            x={labelX}
                            y={labelY}
                            width={structureWorldBounds ? labelBoxW : undefined}
                            align={align}
                            text={labelText}
                            fontSize={fontSize}
                            fontStyle="bold"
                            fill={theme.colors.primary}
                          />
                        </Group>
                      )
                    })()}

                  {showDisplacements &&
                    hasResult &&
                    deformedShape &&
                    deformedShape.polylines.map((pl) => (
                      <Line
                        key={`deformed-${pl.id}`}
                        points={pl.points}
                        stroke={displacementColor}
                        strokeWidth={2 * k}
                        dash={[6 * k, 4 * k]}
                        listening={false}
                      />
                    ))}

                  {showReactions &&
                    hasResult &&
                    (result.reactions || []).map((reaction, index) => {
                      const node = modelNodesById[reaction.node]
                      if (!node) return null
                      const fz = Number(reaction.Fz) || 0
                      const mx = Number(reaction.Mx) || 0
                      const my = Number(reaction.My) || 0
                      if (fz === 0 && mx === 0 && my === 0) return null
                      const tip = modelToWorld(node.x, node.y)
                      const parts = []
                      if (fz !== 0) parts.push(`Fz=${Number(fz.toFixed(2))}`)
                      if (mx !== 0) parts.push(`Mx=${Number(mx.toFixed(2))}`)
                      if (my !== 0) parts.push(`My=${Number(my.toFixed(2))}`)
                      const screenDir = { x: 0, y: fz >= 0 ? -1 : 1 }
                      const tail = { x: tip.x - screenDir.x * 55 * k, y: tip.y - screenDir.y * 55 * k }
                      return (
                        <Group key={`reaction-${index}`} listening={false}>
                          {fz !== 0 && (
                            <Arrow
                              points={[tail.x, tail.y, tip.x, tip.y]}
                              stroke={reactionColor}
                              fill={reactionColor}
                              strokeWidth={3 * k}
                              pointerLength={9 * k}
                              pointerWidth={9 * k}
                            />
                          )}
                          <Text
                            x={tail.x - 10 * k}
                            y={tail.y + 8 * k}
                            text={parts.join(' ')}
                            fontSize={11 * k * textScaleF}
                            fill={reactionColor}
                          />
                        </Group>
                      )
                    })}

                  {marquee &&
                    (() => {
                      const color = marquee.crossing ? marqueeCrossingColor : marqueeWindowColor
                      return (
                        <Rect
                          x={Math.min(marquee.x0, marquee.x1)}
                          y={Math.min(marquee.y0, marquee.y1)}
                          width={Math.abs(marquee.x1 - marquee.x0)}
                          height={Math.abs(marquee.y1 - marquee.y0)}
                          fill={`${color}22`}
                          stroke={color}
                          strokeWidth={1 * k}
                          dash={marquee.crossing ? [6 * k, 4 * k] : undefined}
                          listening={false}
                        />
                      )
                    })()}
                </>
              )}
            </Layer>
          </Stage>
        </StageHost>

        <PropertiesPanel>
          <h4>Propriedades</h4>

          {sectionElementIds && alignmentProfile && (
            <div style={{ marginBottom: '14px' }}>
              <p style={{ fontSize: '13px', margin: '0 0 6px' }}>
                <strong>
                  Corte: {DIAGRAM_MODE_LABELS[diagramMode] || diagramMode} ao longo de {sectionElementIds.length} barra
                  {sectionElementIds.length > 1 ? 's' : ''}
                  {sectionAlignments.length > 1 ? ` (${sectionAlignments.length} trechos combinados)` : ''}
                </strong>
              </p>
              {sectionAlignments.length === 1 && (
                <p style={{ fontSize: '11px', margin: '0 0 6px', opacity: 0.7 }}>
                  Shift+clique em outra barra alinhada para unir os diagramas.
                </p>
              )}
              <AlignmentChart
                profile={alignmentProfile}
                mode={diagramMode}
                perMeter={perMeter && diagramMode !== 'w'}
                theme={theme}
              />
            </div>
          )}

          {!selected && multiSelection.length === 0 && (
            <PropertiesEmptyState>Selecione um item no canvas para editar suas propriedades.</PropertiesEmptyState>
          )}

          {multiSelection.length > 0 &&
            (() => {
              const counts = { pillar: 0, beam: 0, slab: 0, load: 0 }
              multiSelection.forEach((s) => {
                counts[s.type] += 1
              })
              const labels = { pillar: 'pilar', beam: 'viga', slab: 'laje', load: 'carga' }
              const parts = Object.entries(counts)
                .filter(([, count]) => count > 0)
                .map(([type, count]) => `${count} ${labels[type]}${count > 1 ? 's' : ''}`)
              return (
                <div style={{ marginBottom: '14px' }}>
                  <p style={{ fontSize: '13px', margin: '0 0 10px' }}>
                    <strong>{multiSelection.length} itens selecionados</strong>
                    {parts.length > 0 && <span style={{ opacity: 0.7 }}> ({parts.join(', ')})</span>}
                  </p>
                  <ToolButton type="button" onClick={deleteMultiSelection} disabled={disabled}>
                    <FiTrash2 /> Excluir selecionados
                  </ToolButton>
                </div>
              )
            })()}

          {nearbyCandidates.length > 1 && (
            <NearbyList>
              <h5>Itens próximos ao clique</h5>
              {nearbyCandidates.map((candidate) => (
                <NearbyItemButton
                  key={`${candidate.type}-${candidate.key}`}
                  type="button"
                  $active={selected?.type === candidate.type && selected.key === candidate.key}
                  onClick={() => selectCandidate(candidate)}
                >
                  {candidate.label}
                </NearbyItemButton>
              ))}
            </NearbyList>
          )}

          {selected?.type === 'pillar' &&
            (() => {
              const p = pillars.find((x) => x.key === selected.key)
              if (!p) return null
              return (
                <>
                  <PropertyRow>
                    Id
                    <input value={p.id} onChange={(e) => updateSelected('id', e.target.value)} disabled={disabled} />
                  </PropertyRow>
                  <PropertyRow>
                    x
                    <input
                      type="number"
                      value={p.x}
                      onChange={(e) => updateSelected('x', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    y
                    <input
                      type="number"
                      value={p.y}
                      onChange={(e) => updateSelected('y', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    Largura (width)
                    <input
                      type="number"
                      value={p.width}
                      onChange={(e) => updateSelected('width', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    Profundidade (depth)
                    <input
                      type="number"
                      value={p.depth}
                      onChange={(e) => updateSelected('depth', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                </>
              )
            })()}

          {selected?.type === 'beam' &&
            (() => {
              const b = beams.find((x) => x.key === selected.key)
              if (!b) return null
              return (
                <>
                  <PropertyRow>
                    Id
                    <input value={b.id} onChange={(e) => updateSelected('id', e.target.value)} disabled={disabled} />
                  </PropertyRow>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <PropertyRow style={{ flex: 1 }}>
                      xI
                      <input
                        type="number"
                        value={b.xI}
                        onChange={(e) => updateSelected('xI', e.target.value)}
                        disabled={disabled}
                      />
                    </PropertyRow>
                    <PropertyRow style={{ flex: 1 }}>
                      yI
                      <input
                        type="number"
                        value={b.yI}
                        onChange={(e) => updateSelected('yI', e.target.value)}
                        disabled={disabled}
                      />
                    </PropertyRow>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <PropertyRow style={{ flex: 1 }}>
                      xJ
                      <input
                        type="number"
                        value={b.xJ}
                        onChange={(e) => updateSelected('xJ', e.target.value)}
                        disabled={disabled}
                      />
                    </PropertyRow>
                    <PropertyRow style={{ flex: 1 }}>
                      yJ
                      <input
                        type="number"
                        value={b.yJ}
                        onChange={(e) => updateSelected('yJ', e.target.value)}
                        disabled={disabled}
                      />
                    </PropertyRow>
                  </div>
                  <PropertyRow>
                    Largura (b)
                    <input
                      type="number"
                      value={b.width}
                      onChange={(e) => updateSelected('width', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    Altura (h)
                    <input
                      type="number"
                      value={b.height}
                      onChange={(e) => updateSelected('height', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    E
                    <input
                      type="number"
                      value={b.E}
                      onChange={(e) => updateSelected('E', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    ν (Poisson)
                    <input
                      type="number"
                      step={0.01}
                      value={b.nu}
                      onChange={(e) => updateSelected('nu', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                </>
              )
            })()}

          {selected?.type === 'slab' &&
            (() => {
              const s = slabs.find((x) => x.key === selected.key)
              if (!s) return null
              return (
                <>
                  <PropertyRow>
                    Id
                    <input value={s.id} onChange={(e) => updateSelected('id', e.target.value)} disabled={disabled} />
                  </PropertyRow>
                  <PropertyRow>
                    Espessura
                    <input
                      type="number"
                      value={s.thickness}
                      onChange={(e) => updateSelected('thickness', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    E
                    <input
                      type="number"
                      value={s.E}
                      onChange={(e) => updateSelected('E', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    ν (Poisson)
                    <input
                      type="number"
                      step={0.01}
                      value={s.nu}
                      onChange={(e) => updateSelected('nu', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertiesEmptyState>
                    {s.polygon.length} vértices — arraste os pontos destacados no canvas para ajustar o polígono.
                  </PropertiesEmptyState>

                  <div style={{ marginTop: '14px', paddingTop: '10px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', margin: '0 0 10px' }}>Bordas (Moment Release)</p>
                    {(!s.boundaryEdges || s.boundaryEdges.length === 0) && (
                      <PropertiesEmptyState>Nenhuma borda configurada.</PropertiesEmptyState>
                    )}
                    {s.boundaryEdges?.map((edge) => (
                      <div
                        key={edge.key}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          padding: '8px',
                          marginBottom: '8px',
                          backgroundColor: 'rgba(0,0,0,0.03)',
                          borderRadius: '4px',
                        }}
                      >
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="number"
                            min={0}
                            max={s.polygon.length - 1}
                            value={edge.edge}
                            onChange={(e) => updateSlabBoundaryEdge(s.key, edge.key, 'edge', e.target.value)}
                            disabled={disabled}
                            placeholder="edge #"
                            style={{ flex: 1, fontSize: '12px', padding: '4px' }}
                          />
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                            <input
                              type="checkbox"
                              checked={edge.releaseMoment}
                              onChange={(e) =>
                                updateSlabBoundaryEdge(s.key, edge.key, 'releaseMoment', e.target.checked)
                              }
                              disabled={disabled}
                            />
                            Release Moment
                          </label>
                          <button
                            onClick={() => removeSlabBoundaryEdge(s.key, edge.key)}
                            disabled={disabled}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              background: '#ff6b6b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value !== '') {
                          addSlabBoundaryEdge(s.key, Number(e.target.value))
                          e.target.value = ''
                        }
                      }}
                      disabled={disabled}
                      style={{ width: '100%', marginTop: '8px', padding: '6px', fontSize: '12px' }}
                    >
                      <option value="">+ Adicionar borda...</option>
                      {Array.from({ length: s.polygon.length }).map((_, i) => {
                        const isAdded = s.boundaryEdges?.some((e) => Number(e.edge) === i)
                        return (
                          <option key={i} value={i} disabled={isAdded}>
                            Borda {i} ({isAdded ? 'já adicionada' : `vértice ${i} → ${(i + 1) % s.polygon.length}`})
                          </option>
                        )
                      })}
                    </select>
                  </div>
                </>
              )
            })()}

          {selected?.type === 'load' &&
            (() => {
              const l = loads.find((x) => x.key === selected.key)
              if (!l) return null
              return (
                <>
                  <PropertyRow>
                    Id
                    <input value={l.id} onChange={(e) => updateSelected('id', e.target.value)} disabled={disabled} />
                  </PropertyRow>
                  <PropertiesEmptyState>
                    Tipo: {l.type === 'area' ? 'área' : l.type === 'line' ? 'linear' : 'pontual'}
                  </PropertiesEmptyState>
                  {l.type === 'area' && (
                    <>
                      <PropertyRow>
                        Laje
                        <select
                          value={l.slab}
                          onChange={(e) => updateSelected('slab', e.target.value)}
                          disabled={disabled}
                        >
                          <option value="">-</option>
                          {slabs.map((s) => (
                            <option key={s.key} value={s.id}>
                              {s.id}
                            </option>
                          ))}
                        </select>
                      </PropertyRow>
                      <PropertyRow>
                        Valor
                        <input
                          type="number"
                          value={l.value}
                          onChange={(e) => updateSelected('value', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertiesEmptyState>
                        {l.polygon ? 'Possui subárea desenhada.' : 'Sem subárea — atua na laje toda.'}
                      </PropertiesEmptyState>
                      <ToolButton
                        type="button"
                        onClick={() => startPolygonDraw({ type: 'loadPolygon', key: l.key })}
                        disabled={disabled}
                        style={{ width: '100%', justifyContent: 'center', marginTop: '6px' }}
                      >
                        Desenhar subárea
                      </ToolButton>
                      {l.polygon && (
                        <ToolButton
                          type="button"
                          onClick={() => updateSelected('polygon', null)}
                          disabled={disabled}
                          style={{ width: '100%', justifyContent: 'center', marginTop: '6px' }}
                        >
                          Limpar subárea
                        </ToolButton>
                      )}
                    </>
                  )}
                  {l.type === 'line' && (
                    <>
                      <PropertyRow>
                        Viga
                        <select
                          value={l.beam}
                          onChange={(e) => updateSelected('beam', e.target.value)}
                          disabled={disabled}
                        >
                          <option value="">-</option>
                          {beams.map((b) => (
                            <option key={b.key} value={b.id}>
                              {b.id}
                            </option>
                          ))}
                        </select>
                      </PropertyRow>
                      <PropertyRow>
                        Início (m, opcional)
                        <input
                          type="number"
                          placeholder="0"
                          value={l.start}
                          onChange={(e) => updateSelected('start', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        Fim (m, opcional — vazio = fim da viga)
                        <input
                          type="number"
                          placeholder="comprimento da viga"
                          value={l.end}
                          onChange={(e) => updateSelected('end', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        Valor
                        <input
                          type="number"
                          value={l.value}
                          onChange={(e) => updateSelected('value', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                    </>
                  )}
                  {l.type === 'point' && (
                    <>
                      <PropertyRow>
                        x
                        <input
                          type="number"
                          value={l.x}
                          onChange={(e) => updateSelected('x', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        y
                        <input
                          type="number"
                          value={l.y}
                          onChange={(e) => updateSelected('y', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        Valor
                        <input
                          type="number"
                          value={l.value}
                          onChange={(e) => updateSelected('value', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        Mx
                        <input
                          type="number"
                          value={l.Mx}
                          onChange={(e) => updateSelected('Mx', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        My
                        <input
                          type="number"
                          value={l.My}
                          onChange={(e) => updateSelected('My', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                    </>
                  )}
                </>
              )
            })()}

          {selected?.type === 'slabEdge' &&
            (() => {
              const slab = slabs.find((x) => x.key === selected.slabKey)
              const edgeIndex = selected.edgeIndex
              if (!slab || edgeIndex === undefined) return null
              const edge = slab.boundaryEdges?.find((e) => Number(e.edge) === edgeIndex)
              if (!edge) return null
              return (
                <>
                  <div
                    style={{
                      marginBottom: '14px',
                      paddingBottom: '10px',
                      paddingTop: '10px',
                      paddingLeft: '10px',
                      borderBottom: '1px solid rgba(0,0,0,0.1)',
                      borderLeft: edge.releaseMoment ? '4px solid #f59e0b' : '4px solid #8b5cf6',
                      backgroundColor: edge.releaseMoment ? 'rgba(245, 158, 11, 0.05)' : 'rgba(139, 92, 246, 0.05)',
                    }}
                  >
                    <p style={{ fontSize: '13px', fontWeight: '600', margin: '0 0 6px' }}>
                      Bordo de Laje {slab.id} (Borda {edgeIndex})
                    </p>
                    <p style={{ fontSize: '12px', opacity: 0.7, margin: '0' }}>
                      Vértices {edgeIndex} → {(edgeIndex + 1) % slab.polygon.length}
                    </p>
                  </div>
                  <PropertyRow>
                    Id
                    <input
                      value={edge.id}
                      onChange={(e) => updateSlabBoundaryEdge(slab.key, edge.key, 'id', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={edge.releaseMoment}
                        onChange={(e) => updateSlabBoundaryEdge(slab.key, edge.key, 'releaseMoment', e.target.checked)}
                        disabled={disabled}
                      />
                      <span>Liberar Momento (Moment Release)</span>
                    </label>
                  </PropertyRow>
                </>
              )
            })()}

          {selected && selected?.type !== 'slabEdge' && (
            <ToolButton
              type="button"
              onClick={deleteSelected}
              disabled={disabled}
              style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}
            >
              <FiTrash2 /> Remover
            </ToolButton>
          )}

          {hasResult && (
            <PropertiesEmptyState style={{ marginTop: '14px' }}>
              {result.model.diagnostics?.generatedNodeCount ?? result.model.nodes?.length ?? 0} nós e{' '}
              {result.model.diagnostics?.generatedElementCount ?? result.model.elements?.length ?? 0} elementos gerados
              pela malha.
            </PropertiesEmptyState>
          )}
        </PropertiesPanel>
      </CanvasBody>
      {showViewOptions && (
        <ViewOptionsPanel
          snapSize={snapSize}
          setSnapSize={setSnapSize}
          gridSpacingX={gridSpacingX}
          setGridSpacingX={setGridSpacingX}
          gridSpacingY={gridSpacingY}
          setGridSpacingY={setGridSpacingY}
          viewScales={viewScales}
          setViewScales={setViewScales}
          showLoads={showLoads}
          setShowLoads={setShowLoads}
          showNodeLabels={showLabels}
          setShowNodeLabels={setShowLabels}
          showElementLabels={showLabels}
          setShowElementLabels={setShowLabels}
          onClose={() => setShowViewOptions(false)}
        />
      )}
    </CanvasWrapper>
  )
}
